import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthContext = createContext();

// Configure axios defaults
// Auto-detect environment and set appropriate API URL
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const defaultApiUrl = isLocalhost ? 'http://localhost:5000' : window.location.origin;

// Force the local detection if we are NOT on localhost (prevents env vars from overriding live domain)
const finalApiUrl = !isLocalhost ? window.location.origin : (process.env.REACT_APP_API_URL || defaultApiUrl);

axios.defaults.baseURL = finalApiUrl;

console.log('🌐 API URL:', axios.defaults.baseURL);
console.log('🏠 Environment:', isLocalhost ? 'Local Development' : 'Production');

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Set axios authorization header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is authenticated on app load and handle inactivity
  useEffect(() => {
    let inactivityTimer;

    const updateActivity = () => {
      localStorage.setItem('lastActivity', Date.now().toString());
      if (inactivityTimer) clearTimeout(inactivityTimer);

      // Set timer for 2 hours (2 * 60 * 60 * 1000 ms)
      inactivityTimer = setTimeout(() => {
        if (localStorage.getItem('token')) {
          console.log('⏰ Inactivity timeout reached. Logging out...');
          logout();
          toast.error('Session expired due to inactivity. Please log in again.');
        }
      }, 2 * 60 * 60 * 1000);
    };

    const checkInactivity = () => {
      const lastActivity = localStorage.getItem('lastActivity');
      const token = localStorage.getItem('token');

      if (token && lastActivity) {
        const inactiveTime = Date.now() - parseInt(lastActivity);
        const twoHours = 2 * 60 * 60 * 1000;

        if (inactiveTime > twoHours) {
          console.log('⏰ Proactive inactivity logout (app start/refresh)');
          logout();
          toast.error('Your session has expired due to inactivity.');
          return true;
        }
      }
      return false;
    };

    const initAuth = async () => {
      if (token) {
        // If user is already set (e.g. from fresh login), skip the redundant server check
        if (user) {
          console.log('⚡ [Auth] User state already present, skipping redundant check');
          setLoading(false);
          return;
        }

        // Enforce timeout strictly on start/refresh
        if (checkInactivity()) {
          setLoading(false);
          return;
        }

        try {
          console.log('🔍 [Auth] Initializing session check with token:', token?.substring(0, 10) + '...');
          // Explicitly pass token in the first check to prevent race conditions with axios.defaults
          const response = await axios.get('/api/auth/me', {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.success) {
            setUser(response.data.data.user);
            // Start listening for activity once authenticated
            window.addEventListener('mousemove', updateActivity);
            window.addEventListener('keydown', updateActivity);
            window.addEventListener('click', updateActivity);
            window.addEventListener('scroll', updateActivity);
            updateActivity();
          } else {
            console.warn('⚠️ Auth check returned success:false');
            logout();
          }
        } catch (error) {
          console.error('❌ Auth check failed (likely expired session):', error.response?.status, error.message);
          logout();
        }
      }
      setLoading(false);
    };


    initAuth();

    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password
      });

      if (response.data.success) {
        const { token: newToken, user: userData } = response.data.data;
        localStorage.setItem('token', newToken);
        localStorage.setItem('lastActivity', Date.now().toString());
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        setUser(userData);
        toast.success('Login successful!');
        return { success: true };
      } else {
        toast.error(response.data.message || 'Login failed');
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);

      if (response.data.success) {
        // Don't automatically log in - just return success
        toast.success(response.data.message);
        return { success: true, message: response.data.message };
      } else {
        toast.error(response.data.message || 'Registration failed');
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = () => {
    console.log('🚪 [Auth] Clearing session and logging out');
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    setToken(null);
    setUser(null);
    // Explicitly clear axios header
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  // Set up global axios interceptor for 401 errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          // If we have a token but were denied, the session is likely dead
          // We exclude the /api/auth/me check to avoid redundant logouts during initialization
          if (localStorage.getItem('token') && !error.config.url.includes('/api/auth/me')) {
            console.warn('🔒 401 Unauthorized detected globally. Syncing state...');
            logout();
            toast.error('Session expired. Please log in again.');
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/api/auth/profile', profileData);

      if (response.data.success) {
        setUser(response.data.data.user);
        toast.success('Profile updated successfully!');
        return { success: true };
      } else {
        toast.error(response.data.message || 'Profile update failed');
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const isDeveloper = user?.role === 'developer';
  const isTeamMember = ['admin', 'manager', 'developer', 'marketing', 'design', 'support'].includes(user?.role);
  const isClient = user?.role === 'client';

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin,
    isManager,
    isDeveloper,
    isTeamMember,
    isClient,
    isAdminOrManager: isAdmin || isManager,
    login,
    register,
    logout,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};